import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Typography, TablePagination, TableSortLabel, TextField, Box,
  InputAdornment, Paper, IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';
import api from "../api";
import AgregarConductor from "./utilities/AgregarConductor";
import EditarConductor from "./utilities/EditarConductor";
import DetalleConductor from "./utilities/DetalleConductor";

const Conductores = () => {
  const [conductores, setConductores] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false); // New state for edit modal
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConductor, setSelectedConductor] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuConductor, setMenuConductor] = useState(null);

  useEffect(() => {
    fetchConductores();
  }, []);

  const fetchConductores = () => {
    api.get("conductores/")
      .then((response) => {
        setConductores(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener conductores:", error);
      });
  };

  const handleRowClick = (conductor) => {
    setSelectedConductor(conductor);
  };

  const handleDetailClose = () => {
    setSelectedConductor(null);
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este conductor?")) {
      api.delete(`conductores/${id}/`)
        .then(() => {
          setConductores(conductores.filter(conductor => conductor.id !== id));
        })
        .catch(error => {
          console.error("Error al eliminar el conductor:", error);
        });
    }
  };

  const handleMenuOpen = (event, conductor) => {
    setAnchorEl(event.currentTarget);
    setMenuConductor(conductor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuConductor(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null); // Close the menu
  };

  const handleConductorUpdated = (updatedConductor) => {
    setConductores((prevConductores) => {
      const exists = prevConductores.some((i) => i.id === updatedConductor.id);
      if (exists) {
        return prevConductores.map((i) =>
          i.id === updatedConductor.id ? updatedConductor : i
        );
      } else {
        return [...prevConductores, updatedConductor];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  const normalize = (str) =>
    str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredConductores = conductores.filter((conductor) => {
    const search = normalize(searchTerm);

    return (
      normalize(conductor.nombre || "").includes(search) ||
      normalize(conductor.apellido || "").includes(search) ||
      normalize(conductor.numero_licencia || "").includes(search) ||
      normalize(conductor.categoria_licencia || "").includes(search) ||
      normalize(conductor.pin || "").includes(search)
    );
  });

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("conductores/"); // Adjust endpoint if needed for all records
      const allData = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Conductores");

      XLSX.writeFile(workbook, "conductores.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color='#152d51'>
          Conductores
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar conductor"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          <Button onClick={() => setOpen(true)} style={{ border: "1px solid #3f51b5", borderRadius: "6px", color: "#3f51b5", padding: "4px 10px", fontSize: "0.85rem" }}>
            Agregar Conductor <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Apellido</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nro Licencia</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>PIN</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConductores.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((conductor) => (
              <TableRow
                key={conductor.id}
                sx={{ '&:hover': { backgroundColor: '#f1f1f1' }, cursor: "pointer" }}
              >
                <TableCell onClick={() => handleRowClick(conductor)}>{conductor.apellido}</TableCell>
                <TableCell onClick={() => handleRowClick(conductor)}>{conductor.nombre}</TableCell>
                <TableCell onClick={() => handleRowClick(conductor)}>{conductor.numero_licencia}</TableCell>
                <TableCell onClick={() => handleRowClick(conductor)}>{conductor.categoria_licencia}</TableCell>
                <TableCell onClick={() => handleRowClick(conductor)}>{conductor.pin}</TableCell>
                <TableCell>
                  <IconButton onClick={(event) => handleMenuOpen(event, conductor)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredConductores.length}  // <-- use filtered count
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuConductor.id); handleMenuClose(); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>
      <Dialog open={!!selectedConductor} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DetalleConductor
          selectedConductor={selectedConductor}
          vehiculos={conductores}
          handleDetailClose={handleDetailClose}
        />
      </Dialog>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarConductor handleClose={() => setOpen(false)} onUpdate={handleConductorUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary" style={{ fontSize: "16px" }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <EditarConductor conductor={menuConductor} handleClose={() => setOpenEdit(false)} onUpdate={handleConductorUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary" style={{ fontSize: "16px" }}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Conductores;
